import { TabletDiagnosisApi } from 'api/Tablet/TabletdiagnosisApi';
import _ from 'lodash';
import { ChoiceListType } from 'models/Tablet/DiagnosisApiTypes';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoginStore } from 'store/useLoginDataStore';
import Question from './Question';
import Answer from './Answer';
import styled from 'styled-components';
import { usePatientStore } from 'store/usePatientLoginDataStore';
import { useAssessmentStore } from 'store/useAssessmentDataStore';
import { AssessmentQuestionType, AssessmentResponse } from 'models/Tablet/AssessmentApiTypes';
import { TabletAssessmentApi } from 'api/Tablet/TabletAssessmentApi';
import AnswerStatusBadge from './AnswerStatusBadge';
import { font, palette } from 'styles/Global/globalStyles';
import ModalStartExamination from './ModalStartExamination';
import ArrowLineRight from 'assets/Icons/Arrow/ArrowLineRight';
import IconArrowLineRight from 'assets/Icons/Arrow/Icon_arrowLine_right';

interface SubmitBtnStyle {
    opacity: string;
}

const getScrollContainer = () => document.getElementById('tablet-scroll-container');

const StartExamination = () => {
    const { state } = useLocation();

    const [clientRect, setClientRect] = useState<HTMLDivElement[] | null[]>([]);
    const [minusScoreList, setMinusScoreList] = useState<number[]>([]);
    // 검사
    const [assessmentIdList, setAssessmentIdList] = useState<string[]>(state.assessmentIdList);
    const [assessmentStatus, setAssessmentStatus] = useState(0);
    const [assessmentResData, setAssessmentResData] = useState<AssessmentResponse>();
    // 질문 문항
    const [assessmentQuestions, setAssessmentQuestions] = useState<AssessmentQuestionType[]>([]);
    // 유저가 선택한 list
    const [choiceList, setChoiceList] = useState<ChoiceListType[]>([]);
    const [questionInfo, setQuestionInfo] = useState({ prev: 0, now: 0, next: 0, end: 0 });
    const ref = useRef<null[] | HTMLDivElement[]>([]);
    const [missed, setMissed] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // store에 검사 리스트들 저장
    const { assessmentData, setAssessmentData } = useAssessmentStore();
    const { patientData } = usePatientStore();
    const { loginData } = useLoginStore();

    useEffect(() => {
        const clientRect = ref.current;
        setClientRect(clientRect);
    }, []);

    useEffect(() => {
        // K-SNAP_CO_TG 코드일 때만 적용
        if (assessmentResData?.assessmentCode === 'K-SNAP_CO_TG' && !missed && choiceList.length > 0) {
            // 모든 문항에 답변했을 경우 최하단으로 이동
            if (choiceList.length === assessmentQuestions.length) {
                const sc = getScrollContainer();
                if (sc) sc.scrollTo(0, sc.scrollHeight);
                return;
            }

            // 답변하지 않은 문항 찾기
            const totalCnt = assessmentQuestions.length;
            const enteredQuestionNumberList = choiceList.map((item) => item.questionNo);
            const missedQuestionList = findMissingNumber(totalCnt, enteredQuestionNumberList);

            if (missedQuestionList.length > 0) {
                // 답변하지 않은 가장 첫 번째 문항으로 이동
                const firstUnansweredQuestion = missedQuestionList[0];

                // 이미 현재 보고 있는 문항이 첫 번째 미응답 문항이라면 아무것도 하지 않음
                if (questionInfo.now === firstUnansweredQuestion) return;

                setQuestionInfo((prev) => ({
                    ...prev,
                    prev: prev.now,
                    now: firstUnansweredQuestion,
                    next: missedQuestionList.length > 1 ? missedQuestionList[1] : firstUnansweredQuestion + 1,
                }));
            }
        }

        if (missed) {
            const totalCnt = assessmentQuestions.length;
            const enteredQuestionNumberList = choiceList.map((item) => item.questionNo);
            const missedQuestionList = findMissingNumber(totalCnt, enteredQuestionNumberList);
            setQuestionInfo((prev) => ({
                ...prev,
                next: missedQuestionList[1],
                now: missedQuestionList[0],
            }));
        }
    }, [choiceList, missed, assessmentResData?.assessmentCode]);

    useEffect(() => {
        if (choiceList.length === questionInfo.end) {
            const sc = getScrollContainer();
            if (sc) sc.scrollTo(0, sc.scrollHeight);
        } else {
            scrollIntoViewEvent();
        }
    }, [questionInfo]);

    useEffect(() => {
        setChoiceList([]);
        setMinusScoreList([]);
        getAssessmentData();
        const sc = getScrollContainer();
        if (sc) sc.scrollTo(0, 0);
        changeAssessmentStatus(1);
    }, [assessmentStatus]);

    // 현재 해당하는 문항 정보 가져오기
    const getAssessmentData = async () => {
        try {
            const searchAssessmentId = assessmentIdList[assessmentStatus];
            const assessmentListRes = await TabletDiagnosisApi.getAssessmentData(searchAssessmentId, loginData.token);

            if (_.isNull(assessmentListRes) || _.isUndefined(assessmentListRes)) {
                console.log('null');
            } else {
                setAssessmentResData(assessmentListRes!.data);
                setAssessmentQuestions(assessmentListRes!.data.questions.map((question) => question));
                setQuestionInfo((prev) => ({
                    ...prev,
                    now: 1,
                    next: 0,
                    end: assessmentListRes!.data.questions.map((question) => question).length,
                }));
                setAssessmentData({
                    assessmentNameList: assessmentData.assessmentNameList,
                    assessmentGroupList: assessmentData.assessmentGroupList,
                    assessmentIdList: assessmentData.assessmentIdList,
                    assessmentCodeList: assessmentData.assessmentCodeList,
                    assessmentSubNameList: assessmentData.assessmentSubNameList,
                    assessmentCnt: assessmentStatus,
                });
            }
        } catch (error) {
            console.log(error);
        }
    };

    const changeAssessmentStatus = async (status: number) => {
        try {
            const rqData = {
                diagnosisId: state.diagnosisId,
                assessmentCode: assessmentData.assessmentCodeList[assessmentStatus],
                status,
            };
            await TabletAssessmentApi.changeAssessmentStatus(rqData, loginData.token);
        } catch (error) {}
    };

    const changeCompletedAssessmentStatus = async (status: number) => {
        try {
            const requestApi = [];
            for (let i = 0; i < assessmentData.assessmentCodeList.length; i++) {
                const rqData = {
                    diagnosisId: patientData.diagnosisId!,
                    assessmentCode: assessmentData.assessmentCodeList[i],
                    status,
                };
                requestApi.push(setTimeout(() => TabletAssessmentApi.changeAssessmentStatus(rqData, loginData.token), i * 1000));
            }
            await Promise.allSettled(requestApi).then((result) => {
                if (result.filter((item) => item.status === 'rejected').length > 0) {
                    changeCompletedAssessmentStatus(status);
                }
            });
        } catch (error) {
            console.log(error);
        }
    };

    const findMissingNumber = (totalNum: number, enteredNumbers: number[]) => {
        const missedQuestion: number[] = [];

        for (let i = 1; i <= totalNum; i++) {
            if (!enteredNumbers.includes(i)) {
                missedQuestion.push(i);
            }
        }
        return missedQuestion;
    };

    const moveQuestion = (questionNumber: number) => {
        if (!missed) {
            setQuestionInfo((prev) => ({
                ...prev,
                prev: questionNumber!,
                next: questionNumber! + 2,
                now: questionNumber! + 1,
            }));
            setMissed(false);
        }
    };

    const moveMissingQuestion = () => {
        setMissed(true);
    };

    const checkMissingQuestions = () => {
        if (choiceList.length === assessmentQuestions.length) {
            return true;
        }
    };

    const handleSubmit = () => {
        setShowModal(true);
    };

    const scrollIntoViewEvent = useMemo(() => {
        const headerOffset = 100;

        return () => {
            const sc = getScrollContainer();
            if (!sc) return;
            clientRect.forEach((el, index) => {
                if (!_.isNull(el) && index === questionInfo.now - 1) {
                    const elementPosition = clientRect[index]!.getBoundingClientRect().top;
                    const containerTop = sc.getBoundingClientRect().top;
                    const offsetPosition = elementPosition - containerTop + sc.scrollTop - headerOffset;
                    sc.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                    });
                }
            });
        };
    }, [questionInfo]);

    return (
        <Wrapper>
            {showModal && <ModalStartExamination setShowModal={setShowModal} setAssessmentStatus={setAssessmentStatus} assessmentStatus={assessmentStatus} choiceList={choiceList} />}
            {assessmentQuestions ? (
                <>
                    {assessmentQuestions.map((question, index) => (
                        <QuestionContainer key={index} className={`${index.toString()}`} ref={(containerRef) => (ref.current[index] = containerRef)}>
                            <QuestionLayout>
                                <Question question={question.question} index={index} />
                                <Answer
                                    questionInfo={questionInfo}
                                    minusScoreList={minusScoreList}
                                    setMinusScoreList={setMinusScoreList}
                                    answers={question.answers}
                                    questionNumber={question.number}
                                    assessmentCode={assessmentResData?.assessmentCode}
                                    maxNoResCount={assessmentResData?.maxNoResCount}
                                    choiceList={choiceList}
                                    setChoiceList={setChoiceList}
                                    setQuestionInfo={setQuestionInfo}
                                    moveQuestion={moveQuestion}
                                    assessmentQuestions={assessmentQuestions}
                                    assessmentStatus={assessmentStatus}
                                    setAssessmentStatus={setAssessmentStatus}
                                />
                            </QuestionLayout>
                        </QuestionContainer>
                    ))}
                </>
            ) : (
                <div>데이터 없음</div>
            )}
            <SubmitBtn opacity={checkMissingQuestions() ? '' : '.2'} onClick={handleSubmit} disabled={!checkMissingQuestions()}>
                {assessmentData.assessmentCodeList && assessmentData.assessmentCodeList.length - 1 !== assessmentData.assessmentCnt ? (
                    <>
                        <span>제출하고 다음 검사로</span>
                        <IconArrowLineRight />
                    </>
                ) : (
                    <span>제출하고 검사 끝내기</span>
                )}
            </SubmitBtn>
            <AnswerStatusBadge
                choiceListLength={choiceList.length}
                assessmentQuestionsLength={assessmentQuestions.length}
                moveMissingQuestion={moveMissingQuestion}
                assessmentCode={assessmentResData?.assessmentCode}
            />
        </Wrapper>
    );
};

export default StartExamination;

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 100%;
    padding: 68px 0px 52px;
`;

const QuestionContainer = styled.div`
    width: 660px;
    display: flex;
    column-gap: 16px;
    margin: 32px 0px;
`;

const QuestionLayout = styled.div`
    width: 640px;
    display: flex;
    flex-direction: column;
    align-items: start-item;
    row-gap: 20px;
    padding-bottom: 36px;
    border-bottom: 1px solid #dbdbdb;
`;

const IconsContainer = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 50%;
`;
const SubmitBtn = styled.button<SubmitBtnStyle>`
    width: 380px;
    height: 72px;
    border-radius: 4px;
    background-color: #264c97;
    font-size: ${font.size[20]};
    font-weight: ${font.weight.medium};
    color: ${palette.white};
    opacity: ${(props) => props.opacity};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
`;
